/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './calendar.scss';
import './calendarCell.scss';
import './calendarGrid.scss';

import * as React from 'react';
import type { CalendarAria } from 'react-aria';
import type { CalendarState, RangeCalendarState } from 'react-stately';
import type { DateValue } from '@internationalized/date';
import cx from 'classnames';

import { Icon } from 'sarsaparilla';

import { CalendarGrid } from './CalendarGrid';
import type { CalendarLayout, CalendarSize } from './date-picker-types';
import { NextPreviousButton } from './NextPreviousButton';
import { WeekDaysHeader } from './WeekDaysHeader';
import { useStackedInitialScroll } from './date-picker-utils';

/** Display props shared by the range and single-date calendars. */
export interface CalendarFrameDisplayProps {
    numVisibleMonths?: number;
    headingLevel?: 2 | 3 | 4 | 5 | 6;
    /** Controls the calendar cell size via predefined size variants */
    size?: CalendarSize;
    /**
     * `paged` renders the months side by side with next/previous buttons.
     * `stacked` renders every visible month in a vertical column with no
     * next/previous buttons, so the whole range is reached by scrolling.
     */
    layout?: CalendarLayout;
    /**
     * Stacked layout only: when the calendar mounts, scroll its nearest
     * scrolling ancestor so this date's month sits just below the sticky
     * weekday row. Does nothing when the date is in the first visible month.
     */
    initialScrollDate?: DateValue | null;
}

interface CalendarFrameProps extends Required<
    Omit<CalendarFrameDisplayProps, 'initialScrollDate'>
> {
    initialScrollDate?: DateValue | null;
    state: CalendarState | RangeCalendarState;
    /** The result of `useCalendar` or `useRangeCalendar`. */
    aria: CalendarAria;
    /** Ref for the calendar element; must be the one given to `useRangeCalendar`. */
    calendarRef: React.RefObject<HTMLDivElement>;
}

/**
 * Markup shared by the range and single-date calendars: the header (or sticky
 * weekday row when stacked), the month grids, and the stacked initial scroll.
 */
export function CalendarFrame({
    state,
    aria,
    calendarRef,
    numVisibleMonths,
    headingLevel,
    size,
    layout,
    initialScrollDate,
}: CalendarFrameProps) {
    const { calendarProps, prevButtonProps, nextButtonProps, title } = aria;
    const Title = `h${headingLevel}` as const;

    useStackedInitialScroll(
        calendarRef,
        layout,
        initialScrollDate,
        state.visibleRange.start
    );

    return (
        <div
            className={cx(
                'sui--calendar',
                `calendar-size-${size}`,
                `calendar-layout-${layout}`
            )}
        >
            <div {...calendarProps} ref={calendarRef} className="calendar" translate="no">
                {layout === 'stacked' ? (
                    <>
                        <Title className="rec-sr-only">{title}</Title>
                        <WeekDaysHeader state={state} />
                    </>
                ) : (
                    <div className="calendar-header-group">
                        <NextPreviousButton {...prevButtonProps}>
                            <Icon iconName="chevron-left" />
                        </NextPreviousButton>
                        <Title className="rec-sr-only">{title}</Title>
                        <NextPreviousButton {...nextButtonProps}>
                            <Icon iconName="chevron-right" />
                        </NextPreviousButton>
                    </div>
                )}

                <div className="calendar-grids-container">
                    {Array(numVisibleMonths)
                        .fill(null)
                        .map((_, i) => (
                            <CalendarGrid
                                key={i}
                                state={state}
                                offset={{ months: i }}
                                showWeekDays={layout !== 'stacked'}
                                layout={layout}
                            />
                        ))}
                </div>
            </div>
        </div>
    );
}
